import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FORMAT_SIZE, alertWords, isOwnCeiling, sceneWords, shotRerun, type CallView, type ProjectView, type ShotView } from "@joogoos/contracts";
import { api } from "../../api.js";
import { Icon } from "../../ui/icons.js";
import { sceneLabel } from "./SceneWords.js";

/**
 * The generating stage as a node canvas (docs/03). One node per operation, columns by stage,
 * edges showing what feeds what. It is a VIEW, not an editor: you can select a node and rerun
 * its shot, but nothing rewires the graph — the shape comes from the script, which is what lets
 * the guards in ADR 0009 hold.
 */
const NW = 142, COL = 162, ROW = 128, TOP = 20, PORT_Y = 34;
const COLX = { brief: 10, script: 10 + COL, picture: 10 + COL * 2, motion: 10 + COL * 3, voice: 10 + COL * 4, caption: 10 + COL * 5 };
type State = "done" | "running" | "queued" | "failed";
const DOT: Record<State, string> = { done: "bg-good", running: "bg-coral", queued: "bg-wait", failed: "bg-bad" };

type N = { id: string; x: number; y: number; title: string; op: string; state: State; cost: string; shotId?: string; body: React.ReactNode };

export function Pipeline({ project }: { project: ProjectView }) {
  const [sel, setSel] = useState<string | null>(null);
  const { nodes, edges, height } = useMemo(() => build(project), [project]);
  const selected = nodes.find((n) => n.id === sel) ?? null;
  const shot = selected?.shotId ? project.shots.find((s) => s.id === selected.shotId) ?? null : null;

  return (
    <div className="p-5 flex flex-col gap-3 h-full min-h-0">
      {project.status === "failed" ? <Stopped project={project} /> : <Refused project={project} />}
      <Canvas nodes={nodes} edges={edges} height={height} sel={sel} onSelect={setSel} />
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-xs">
          <Legend cls="bg-good" label="done" /><Legend cls="bg-coral" label="running" /><Legend cls="bg-wait" label="queued" />
        </div>
        <p className="text-[11.5px] text-muted max-w-2xl leading-snug">
          The shape comes from the script — you cannot rewire it. Select a node to rerun its shot; anything
          already sent to a provider finishes and is charged.
        </p>
        {shot ? <RerunNode projectId={project.id} shot={shot} title={selected!.title} /> : null}
      </div>
    </div>
  );
}

/**
 * Why a step did not run, in the customer's words.
 *
 * The gap [14](../../../../../docs/14-open-items.md) listed as "a refused step shows no message in the Studio". Every
 * guard has written an `alerts` row since the day they were built ([13](../../../../../docs/13-cost-guards.md)) and
 * nothing in the customer's app has ever read one — so a post that stopped at a cap simply stopped, with a node
 * that stayed grey and no sentence anywhere on the screen saying what happened. That is the worst possible way for
 * a product that spends somebody's money to behave.
 *
 * Only this project's, only the newest, and only what the person can do something about. `alertWords` lives in
 * contracts so the same refusal is described once (ADR 0016), and a ceiling the customer set themselves comes with
 * the one tap that raises it (`RaiseBudget` for a post's cap, `AtYourLimit` for the day and the month).
 */
function Refused({ project }: { project: ProjectView }) {
  const alerts = useQuery({ queryKey: ["alerts"], queryFn: api.alerts });
  // Newest first, and only ones about this post: another project's cap is not this screen's business.
  const mine = (alerts.data ?? []).filter((a) => a.projectId === project.id);
  const latest = mine[0];
  if (!latest) return null;
  // The post's own cap is said only while the post is stopped at it: once raised, or once finished, the alert is
  // history, and the banner went on saying "reached the cap" over a post that was making it again (dev box, 2026-09-25).
  if (latest.kind === "project_budget" && project.status !== "over_budget") return null;

  return (
    <div className={`rounded-[10px] px-3.5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] ${
      isOwnCeiling(latest.kind) || latest.kind === "project_budget" ? "bg-tint text-ink" : "bg-panel border border-line text-ink"
    }`} role="status">
      <span className="min-w-0">{alertWords(latest.kind)}</span>
      {latest.kind === "project_budget" ? <RaiseBudget project={project} /> : null}
    </div>
  );
}

/**
 * Raise this post's cap, in one press (guard 5; [ADR 0012](../../../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md) point 3).
 *
 * The cap on a post is ours, not the customer's: it is set when the script is written, to twice what the shots were
 * estimated at, and it exists to stop a loop inside one post rather than to limit anybody. So raising it needs no
 * number typed — it goes up by half and the post carries on. Every other guard is still underneath, so this cannot
 * outrun the day, the month or the balance.
 */
function RaiseBudget({ project }: { project: ProjectView }) {
  const qc = useQueryClient();
  const raise = useMutation({
    mutationFn: () => api.raiseBudget(project.id),
    onSuccess: (p) => { qc.setQueryData(["project", project.id], p); void qc.invalidateQueries({ queryKey: ["alerts"] }); },
  });
  return (
    <span className="ml-auto flex items-center gap-2">
      <button
        onClick={() => raise.mutate()}
        disabled={raise.isPending}
        className="shrink-0 px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-60"
      >
        {raise.isPending ? "Raising…" : "Raise this post's cap"}
      </button>
      {raise.error ? <span role="alert" className="text-[12px] text-bad">{String(raise.error)}</span> : null}
    </span>
  );
}

/**
 * A post whose step gave up after its retries (2026-09-25), with the one press that carries on. Before this the post
 * sat "making it" for good, and the header span for two hours. What stopped is read off the post itself: no script
 * yet, shots still missing something, or everything made and the render to do.
 */
export function stoppedAt(project: ProjectView): string {
  if (!project.script) return "the script";
  const missing = project.shots.filter((s) => s.status !== "ready").length;
  if (missing) return `${missing} of ${project.shots.length} shot${project.shots.length > 1 ? "s" : ""}`;
  return "the render";
}

/**
 * What kept failing, in the customer's words. A render is our own: it said "a provider kept failing" when the renderer
 * could not fetch a sound (dev box, 2026-09-26), which sends a customer looking for someone else to blame.
 */
export function whyStopped(project: ProjectView): string {
  if (!project.script) return "the service that writes it kept failing";
  if (project.shots.some((s) => s.status !== "ready")) return "the service that makes them kept failing";
  return "putting the video together kept failing on our side";
}

function Stopped({ project }: { project: ProjectView }) {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.retry(project.id),
    onSuccess: (p) => { qc.setQueryData(["project", project.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); void qc.invalidateQueries({ queryKey: ["activity"] }); },
  });
  return (
    <div className="rounded-[10px] px-3.5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] bg-tint text-ink" role="status">
      <span className="min-w-0">
        This stopped at {stoppedAt(project)}: {whyStopped(project)}, so nothing more was tried. A failed attempt is not
        charged. Try again redoes only what is missing, charged like the first run.
      </span>
      <span className="ml-auto flex items-center gap-2">
        <button onClick={() => retry.mutate()} disabled={retry.isPending} className="shrink-0 px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-60">
          {retry.isPending ? "Starting…" : "Try again"}
        </button>
        {retry.error ? <span role="alert" className="text-[12px] text-bad">{String(retry.error)}</span> : null}
      </span>
    </div>
  );
}

function Legend({ cls, label }: { cls: string; label: string }) {
  return <span className="flex items-center gap-1.5 text-muted"><span className={`w-2 h-2 rounded-full ${cls}`} />{label}</span>;
}

/** Pan with a drag, zoom with the wheel. The graph is bigger than the pane by design. */
function Canvas({ nodes, edges, height, sel, onSelect }: {
  nodes: N[]; edges: Array<[number, number, number, number, boolean]>; height: number;
  sel: string | null; onSelect: (id: string | null) => void;
}) {
  const [t, setT] = useState({ x: 0, y: 0, z: 0.86 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const width = COLX.caption + NW + 40;

  /** Fit means fit: the whole graph, centred, with a little air. */
  const fit = useCallback(() => {
    const el = box.current;
    if (!el) return;
    const z = Math.min(1.4, Math.max(0.3, Math.min((el.clientWidth - 24) / width, (el.clientHeight - 24) / height)));
    setT({ z, x: (el.clientWidth - width * z) / 2, y: (el.clientHeight - height * z) / 2 });
  }, [width, height]);

  useLayoutEffect(fit, [fit]);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  return (
    <div
      ref={box}
      className="relative flex-1 min-h-0 rounded-xl border border-line overflow-hidden bg-[#f2efea] [background-image:radial-gradient(#dcd6cd_1px,transparent_1px)] [background-size:18px_18px] cursor-grab active:cursor-grabbing"
      onPointerDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, ox: t.x, oy: t.y }; (e.target as Element).setPointerCapture?.(e.pointerId); }}
      onPointerMove={(e) => { const d = drag.current; if (d) setT((p) => ({ ...p, x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) })); }}
      onPointerUp={() => { drag.current = null; }}
      onWheel={(e) => setT((p) => ({ ...p, z: Math.min(1.6, Math.max(0.4, p.z - e.deltaY * 0.0012)) }))}
    >
      <div className="absolute origin-top-left" style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.z})`, width, height }}>
        <svg width={width} height={height} className="absolute inset-0 pointer-events-none">
          {edges.map(([x1, y1, x2, y2, live], i) => {
            const dx = Math.max(28, (x2 - x1) * 0.45);
            return <path key={i} d={`M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`} fill="none" stroke={live ? "#ff5143" : "#c2bab0"} strokeWidth={live ? 2 : 1.5} strokeDasharray={live ? "5 4" : undefined} />;
          })}
        </svg>
        {nodes.map((n) => (
          <button
            key={n.id}
            onClick={(e) => { e.stopPropagation(); onSelect(n.id === sel ? null : n.id); }}
            className={`absolute w-[142px] text-left bg-panel border rounded-[9px] overflow-hidden shadow-[0_2px_6px_rgba(23,23,26,.09)] ${
              n.id === sel ? "border-coral ring-2 ring-tint" : "border-line-2"
            }`}
            style={{ left: n.x, top: n.y }}
          >
            <div className="flex items-center gap-1.5 px-1.5 py-1 bg-ink text-white">
              <span className={`w-2 h-2 rounded-full ${DOT[n.state]}`} />
              <span className="text-[9.5px] font-bold uppercase tracking-[.05em] truncate">{n.title}</span>
            </div>
            <div className="px-1.5 py-1.5">{n.body}</div>
            <div className="flex items-center gap-1.5 px-1.5 py-1 border-t border-line text-[9.5px] text-muted">
              <span className="truncate">{n.op}</span>
              <span className="ml-auto tabular-nums">{n.cost}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="absolute left-3 bottom-3 flex items-center gap-0.5 bg-panel border border-line rounded-lg p-1 shadow-sm text-ink-2">
        <button onClick={() => setT((p) => ({ ...p, z: Math.max(0.4, p.z - 0.1) }))} className="px-2 py-0.5"><Icon name="x" size={13} /></button>
        <span className="px-1 text-[11px] tabular-nums">{Math.round(t.z * 100)}%</span>
        <button onClick={() => setT((p) => ({ ...p, z: Math.min(1.6, p.z + 0.1) }))} className="px-2 py-0.5"><Icon name="plus" size={13} /></button>
        <button onClick={fit} className="px-2 py-0.5 text-[11px] border-l border-line">Fit</button>
      </div>
      <div className="absolute left-3 top-3 flex items-center gap-2 bg-panel/95 border border-line rounded-lg px-2.5 py-1.5 text-[11px] shadow-sm">
        <b>{nodes.length} steps</b><span className="text-muted">drag to pan · scroll to zoom</span>
      </div>
    </div>
  );
}

/**
 * Rerun the selected node's shot, with its price on the button as every other paid press has (2026-10-12), and only
 * where the server has something to make again (`shotRerun`): a drawn scene or a toured photo that says nothing was
 * offered a Rerun the server refused.
 */
function RerunNode({ projectId, shot, title }: { projectId: string; shot: ShotView; title: string }) {
  const qc = useQueryClient();
  const rerun = useMutation({
    mutationFn: () => api.rerun(projectId, shot.id),
    onSuccess: (p) => qc.setQueryData(["project", projectId], p),
  });
  const redo = shotRerun(shot);
  if (!redo.picture && !redo.voice) return null;
  return (
    <button onClick={() => rerun.mutate()} disabled={rerun.isPending} className="ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page disabled:opacity-50">
      <Icon name="refresh" size={12} /> {rerun.isPending ? "Queued…" : `Rerun ${title} · about $${redo.usd.toFixed(2)}`}
    </button>
  );
}

/** Turn the project into nodes and edges. Nothing here decides the shape; it reads it. */
function build(project: ProjectView) {
  const nodes: N[] = [];
  const edges: Array<[number, number, number, number, boolean]> = [];
  const out = (x: number, y: number) => [x + NW, y + PORT_Y] as const;
  const inn = (x: number, y: number) => [x, y + PORT_Y] as const;
  const link = (a: readonly [number, number], b: readonly [number, number], live = false) => edges.push([a[0], a[1], b[0], b[1], live]);
  const money = (cs: CallView[]) => {
    const charged = cs.reduce((n, c) => n + Number(c.chargedUsd ?? 0), 0);
    const held = cs.reduce((n, c) => n + Number(c.reservedUsd ?? 0), 0);
    return charged > 0 ? `$${charged.toFixed(2)}` : held > 0 ? `$${held.toFixed(2)} held` : "—";
  };
  const callsFor = (op: string, shotId?: string) =>
    project.calls.filter((c) => c.operation === op && (shotId ? c.shotId === shotId : true));

  const d = project.draft;
  const briefY = TOP + Math.max(0, (project.shots.length - 1) * ROW) / 2;
  nodes.push({
    id: "brief", x: COLX.brief, y: briefY, title: "Brief", op: "the recap", state: "done", cost: "—",
    body: <p className="text-[10px] leading-snug text-ink-2">{[d.idea, d.format, d.sourceMediaIds?.length ? `${d.sourceMediaIds.length} photos` : null].filter(Boolean).join(" · ")}</p>,
  });
  const scriptCalls = callsFor("write_script");
  nodes.push({
    id: "script", x: COLX.script, y: briefY, title: project.script ? `Script v${project.script.version}` : "Script", op: "write_script",
    state: project.script ? "done" : scriptCalls.some((c) => c.status === "running") ? "running" : "queued", cost: money(scriptCalls),
    body: <p className="text-[10px] leading-snug text-ink-2">{project.script ? `${project.shots.length} shots · ${(project.script.durationMs / 1000).toFixed(0)} s · ${project.script.tone}` : "waiting for the brief"}</p>,
  });
  link(out(COLX.brief, briefY), inn(COLX.script, briefY));

  project.shots.forEach((s, i) => {
    const y = TOP + i * ROW;
    const name = `${s.position + 1} · ${s.type}`;
    // A toured photo is the owner's own, as it is: no call makes it (`tour.ts`).
    const picOp = s.motion === "tour" ? "your photo" : s.sourceMediaId ? "edit_image" : "generate_image";
    const picCalls = [...callsFor("edit_image", s.id), ...callsFor("generate_image", s.id)];
    const pic = s.assets.image?.media;
    // A drawn scene is made by the renderer, not a model: nothing to wait for and nothing to pay (ADR 0050). A photo
    // piece is drawn over the owner's photo, which no call made either, so the node shows it and says whose it is, with
    // the words under it (2026-09-29).
    const onPhoto = pic && s.scene?.kind === "showreel" && s.scene.piece === "photo";
    nodes.push(s.scene ? {
      id: `pic-${s.id}`, x: COLX.picture, y, title: name, op: onPhoto ? "drawn on your photo" : "drawn", shotId: s.id, state: "done", cost: "—",
      body: onPhoto
        ? <><Thumb url={pic.thumbUrl} label={sceneWords(s.scene)} /><p className="mt-1 text-[10px] leading-snug text-ink-2 line-clamp-1">{sceneWords(s.scene)}</p></>
        : <p className="text-[10px] leading-snug text-ink-2 line-clamp-3">{sceneLabel(s.scene.kind)} · {sceneWords(s.scene)}</p>,
    } : {
      id: `pic-${s.id}`, x: COLX.picture, y, title: name, op: picOp, shotId: s.id,
      state: pic ? "done" : s.status === "failed" ? "failed" : picCalls.some((c) => c.status === "running") ? "running" : "queued",
      cost: money(picCalls), body: <Thumb url={pic?.thumbUrl} label={s.description} />,
    });
    link(out(COLX.script, briefY), inn(COLX.picture, y), s.status === "generating" && !pic);

    if (s.motion === "tour") {
      nodes.push({
        id: `mot-${s.id}`, x: COLX.motion, y, title: "motion", op: "toured", shotId: s.id, state: pic ? "done" : "queued", cost: "—",
        body: <p className="text-[10px] leading-snug text-ink-2">toured by our camera, for nothing</p>,
      });
      link(out(COLX.picture, y), inn(COLX.motion, y), false);
    }
    if (s.motion === "image_to_video") {
      const mCalls = callsFor("image_to_video", s.id);
      const vid = s.assets.video?.media;
      nodes.push({
        id: `mot-${s.id}`, x: COLX.motion, y, title: "motion", op: "image_to_video", shotId: s.id,
        state: vid ? "done" : mCalls.some((c) => c.status === "running" || c.status === "parked") ? "running" : "queued",
        cost: money(mCalls), body: <Thumb url={vid?.thumbUrl} label="animating the picture" />,
      });
      link(out(COLX.picture, y), inn(COLX.motion, y), !vid && !!pic);
    }

    const vCalls = callsFor("tts", s.id);
    const voice = s.assets.voice?.media;
    nodes.push({
      id: `voi-${s.id}`, x: COLX.voice, y, title: "voice", op: "tts", shotId: s.id,
      state: voice ? "done" : vCalls.some((c) => c.status === "running") ? "running" : "queued", cost: money(vCalls),
      body: <p className="text-[10px] leading-snug text-ink-2 line-clamp-3">{s.dialogue ? `“${s.dialogue}”` : "no line"}</p>,
    });
    link(out(COLX.script, briefY), inn(COLX.voice, y));

    // The voice brings its words' timings with it (docs/30), so a line is aligned only when it came back without them:
    // the node names the step that actually timed the captions. A video of pieces has none, its type is the caption
    // (ADR 0058), so there the same timings are what its pieces land on, and the node says so (review, 2026-09-28).
    const cCalls = callsFor("align", s.id);
    const showreel = !!project.script?.showreel;
    nodes.push({
      id: `cap-${s.id}`, x: COLX.caption, y, title: showreel ? "timing" : "captions", op: cCalls.length ? "align" : "timed by the voice", shotId: s.id,
      state: s.assets.captions ? "done" : cCalls.some((c) => c.status === "running") ? "running" : "queued", cost: money(cCalls),
      body: <p className="text-[10px] leading-snug text-ink-2 line-clamp-3">{showreel ? (s.dialogue ? "the voice's words, timed; no captions" : "nothing said") : s.onScreenText ?? (s.dialogue ? "word by word" : "nothing on screen")}</p>,
    });
    link(out(COLX.voice, y), inn(COLX.caption, y));
  });

  const tailY = TOP + Math.max(1, project.shots.length) * ROW + 24;
  // No music node: a video's music is composed by code in the worker, for nothing (ADR 0054), so there is no call to show.
  const ready = project.shots.filter((s) => s.status === "ready").length;
  nodes.push({
    id: "compose", x: COLX.voice, y: tailY, title: "compose", op: "compose",
    state: project.renders.length ? "done" : "queued", cost: "—",
    body: <p className="text-[10px] text-ink-2 leading-snug">waits for every shot<br />{ready} of {project.shots.length} ready</p>,
  });
  // Renders come newest first (the project snapshot): the newest is what the post is at, an older one a try before it.
  const render = project.renders[0];
  nodes.push({
    id: "render", x: COLX.caption, y: tailY, title: "render", op: "render",
    state: render?.status === "succeeded" ? "done" : render?.status === "failed" ? "failed" : render ? "running" : "queued",
    cost: money(callsFor("render")),
    // The project's own shape, as compose lays it out (`FORMAT_SIZE`): this said a story's size over every project, a
    // square post included (the first pilot round, 2026-10-05).
    body: <p className="text-[10px] text-ink-2 leading-snug">{render?.error ?? `${FORMAT_SIZE[project.format ?? "story"].width}×${FORMAT_SIZE[project.format ?? "story"].height} · Remotion`}</p>,
  });
  link(out(COLX.voice, tailY), inn(COLX.caption, tailY));
  project.shots.forEach((_, i) => link(out(COLX.caption, TOP + i * ROW), inn(COLX.voice, tailY)));

  return { nodes, edges, height: tailY + 120 };
}

/**
 * A node's picture, drawn from its small copy (ADR 0051). A clip is shown by its poster frame, not played: a canvas
 * of six autoplaying videos downloaded and decoded all six in full to draw 44 px of each.
 */
function Thumb({ url, label }: { url: string | undefined; label: string }) {
  if (!url) return <div className="h-11 rounded border border-dashed border-line-2 grid place-items-center text-[10px] text-faint px-1 text-center">{label.slice(0, 40)}</div>;
  return <img src={url} alt="" loading="lazy" decoding="async" className="h-11 w-full object-cover rounded" />;
}
